// Drop-in vervanging voor @supabase/supabase-js
// Stuurt alle calls naar de eigen Express API op /api/db/:table

const API_BASE = '/api/db'

type Row = Record<string, any>

class QueryBuilder {
  private _table: string
  private _op: 'select' | 'insert' | 'update' | 'delete' | 'upsert' = 'select'
  private _cols = '*'
  private _filters: string[] = []
  private _orders: string[] = []
  private _limitN?: number
  private _single = false
  private _insertData?: Row[]
  private _updateData?: Row
  private _upsertPayload?: any
  private _upsertConflict?: string
  private _orFilters: Array<{ col: string; op: string; val: string }> = []

  constructor(table: string) { this._table = table }

  select(cols = '*') { this._op = 'select'; this._cols = cols; return this }
  insert(data: Row | Row[]) { this._op = 'insert'; this._insertData = Array.isArray(data) ? data : [data]; return this }
  update(data: Row) { this._op = 'update'; this._updateData = data; return this }
  upsert(data: Row | Row[], opts?: { onConflict?: string }) {
    this._op = 'upsert'; this._upsertPayload = data; this._upsertConflict = opts?.onConflict; return this
  }
  delete() { this._op = 'delete'; return this }
  eq(col: string, val: any) { this._filters.push(`${col}:eq:${val}`); return this }
  neq(col: string, val: any) { this._filters.push(`${col}:neq:${val}`); return this }
  gt(col: string, val: any) { this._filters.push(`${col}:gt:${val}`); return this }
  gte(col: string, val: any) { this._filters.push(`${col}:gte:${val}`); return this }
  lt(col: string, val: any) { this._filters.push(`${col}:lt:${val}`); return this }
  lte(col: string, val: any) { this._filters.push(`${col}:lte:${val}`); return this }
  or(conditions: string) {
    // Parse "col.op.val,col.op.val" — split by comma, then by first two dots
    conditions.split(',').forEach(c => {
      const m = c.trim().match(/^([^.]+)\.([^.]+)\.(.+)$/)
      if (m) this._orFilters.push({ col: m[1], op: m[2], val: m[3] })
    })
    return this
  }
  in(col: string, vals: any[]) {
    // Implement as multiple OR eq conditions
    vals.forEach(v => this._orFilters.push({ col, op: 'eq', val: String(v) }))
    return this
  }
  order(col: string, opts?: { ascending?: boolean }) {
    this._orders.push(`${col}:${opts?.ascending === false ? 'desc' : 'asc'}`); return this
  }
  limit(n: number) { this._limitN = n; return this }
  single() { this._single = true; return this._exec() }
  then(resolve: (v: any) => any, reject?: (e: any) => any) { return this._exec().then(resolve, reject) }

  private async _exec(): Promise<{ data: any; error: any }> {
    try {
      // When OR conditions exist: run multiple queries and merge unique rows by id
      if (this._orFilters.length > 0 && this._op === 'select') {
        const results = await Promise.all(this._orFilters.map(async f => {
          const params = new URLSearchParams()
          if (this._cols !== '*') params.set('select', this._cols)
          this._filters.forEach(fi => params.append('filter', fi))
          params.append('filter', `${f.col}:${f.op}:${f.val}`)
          this._orders.forEach(o => params.append('order', o))
          if (this._limitN !== undefined) params.set('limit', String(this._limitN))
          const resp = await fetch(`${API_BASE}/${encodeURIComponent(this._table)}?${params}`)
          if (!resp.ok) return []
          return resp.json()
        }))
        const seen = new Set<string>()
        const data = results.flat().filter((row: any) => {
          const key = String(row?.id ?? JSON.stringify(row))
          if (seen.has(key)) return false
          seen.add(key)
          return true
        })
        // Re-apply ordering client-side if needed
        if (this._orders.length > 0) {
          const [firstOrder] = this._orders
          const [orderCol, dir] = firstOrder.split(':')
          data.sort((a: any, b: any) => {
            const va = a[orderCol], vb = b[orderCol]
            if (va < vb) return dir === 'asc' ? -1 : 1
            if (va > vb) return dir === 'asc' ? 1 : -1
            return 0
          })
        }
        return { data, error: null }
      }

      const params = new URLSearchParams()
      if (this._cols !== '*') params.set('select', this._cols)
      this._filters.forEach(f => params.append('filter', f))
      this._orders.forEach(o => params.append('order', o))
      if (this._limitN !== undefined) params.set('limit', String(this._limitN))
      if (this._single) params.set('single', '1')

      const base = `${API_BASE}/${encodeURIComponent(this._table)}`
      let url = base
      let method = 'GET'
      let body: string | undefined

      switch (this._op) {
        case 'select': url += `?${params}`; break
        case 'insert': method = 'POST'; body = JSON.stringify(this._insertData); break
        case 'update': url += `?${params}`; method = 'PATCH'; body = JSON.stringify(this._updateData); break
        case 'upsert': method = 'PUT'; body = JSON.stringify({ data: this._upsertPayload, conflict: this._upsertConflict }); break
        case 'delete': url += `?${params}`; method = 'DELETE'; break
      }

      const resp = await fetch(url, {
        method,
        headers: body ? { 'Content-Type': 'application/json' } : {},
        body,
      })

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ message: resp.statusText }))
        return { data: null, error: err }
      }
      return { data: await resp.json(), error: null }
    } catch (e: any) {
      return { data: null, error: { message: e.message } }
    }
  }
}

// Realtime vervangen door polling (elke 15 seconden)
class Channel {
  private _cbs: Array<(p: any) => void> = []
  private _iv?: ReturnType<typeof setInterval>
  on(_ev: string, _cfg: any, cb: (p: any) => void) { this._cbs.push(cb); return this }
  subscribe() {
    this._iv = setInterval(() => this._cbs.forEach(cb => cb({ eventType: 'UPDATE', new: {}, old: {} })), 15000)
    return this
  }
  unsubscribe() { if (this._iv) clearInterval(this._iv) }
}

class StorageBucket {
  constructor(private bucket: string) {}

  async upload(path: string, file: File | Blob) {
    const form = new FormData()
    // De servernaam wordt afgeleid van de extensie van originalname, dus die moet mee.
    form.append('file', file, path.split('/').pop() || 'upload')
    form.append('bucket', this.bucket)
    form.append('path', path)
    const resp = await fetch('/api/storage/upload', { method: 'POST', body: form })
    if (!resp.ok) return { data: null, error: { message: 'Upload mislukt' } }
    // De server hernoemt het bestand; alleen die naam is later terug te vinden.
    const body = await resp.json().catch(() => ({}))
    return { data: { path: body.path as string, fullPath: body.fullPath as string }, error: null }
  }

  getPublicUrl(path: string) {
    const filename = path.split('/').pop()
    return { data: { publicUrl: `/api/storage/${this.bucket}/${filename}` } }
  }

  async remove(paths: string[]) {
    await fetch('/api/storage/delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bucket: this.bucket, paths }),
    })
    return { error: null }
  }
}

export const supabase = {
  supabaseUrl: '/api',
  from: (table: string) => new QueryBuilder(table),
  channel: (_name: string) => new Channel(),
  removeChannel: (ch: Channel) => ch.unsubscribe(),
  storage: { from: (bucket: string) => new StorageBucket(bucket) },
}

// Type-exports voor compatibiliteit met bestaande componenten
export type UserRole = 'admin' | 'sales' | 'teamleader' | 'forklift' | 'technical'

export type User = {
  id: string
  name: string
  email?: string
  role: UserRole
  pin: string
  selected?: any
  receives_daily_reports?: boolean
  receives_weekly_reports?: boolean
  created_at: string
}

export type Database = any
